import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Mail,
  User,
  Phone,
  Briefcase,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  Plus,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

// Digital Isaac Avatar (Option 2 - with data screens)
const DIGITAL_ISAAC = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/f6367888bb9bb3d7bdad4fd836f9d151ec32ceb848a5b100c0a41679671f3cb1.png";

const DEPARTMENTS = [
  "Supply Chain",
  "Materials Management",
  "Facilities Management",
  "Biomedical Engineering",
  "Environmental Services",
  "Nursing Administration",
  "Operating Room",
  "Emergency Department",
  "Radiology",
  "Laboratory",
  "Pharmacy",
  "IT/Technology",
  "Administration",
  "Other"
];

export default function HealthSystemRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email check, 2: Registration form
  const [loading, setLoading] = useState(false);
  const [healthSystem, setHealthSystem] = useState(null);
  const [showNewFacility, setShowNewFacility] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    title: "",
    department: "",
    facility_id: "",
    // New facility fields
    new_facility_name: "",
    new_facility_type: "clinic",
    new_facility_address: "",
    new_facility_city: "",
    new_facility_state: "",
    new_facility_zip: ""
  });

  const checkEmailDomain = async () => {
    if (!formData.email || !formData.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/health-systems/check-domain/${encodeURIComponent(formData.email)}`);
      
      if (response.data.found) {
        setHealthSystem(response.data);
        setStep(2);
        toast.success(`Found: ${response.data.system_name}`);
      } else {
        toast.error("Your email domain is not associated with a registered health system. Please contact your administrator.");
      }
    } catch (error) {
      toast.error("Unable to verify email domain. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Please enter your full name");
      return;
    }
    
    if (!formData.facility_id && !showNewFacility) {
      toast.error("Please select your facility or add a new one");
      return;
    }
    
    if (showNewFacility && !formData.new_facility_name) {
      toast.error("Please enter the facility name");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone || null,
        title: formData.title || null,
        department: formData.department || null,
        facility_id: showNewFacility ? null : formData.facility_id,
        new_facility_name: showNewFacility ? formData.new_facility_name : null,
        new_facility_type: showNewFacility ? formData.new_facility_type : null,
        new_facility_address: showNewFacility ? formData.new_facility_address : null,
        new_facility_city: showNewFacility ? formData.new_facility_city : null,
        new_facility_state: showNewFacility ? formData.new_facility_state : null,
        new_facility_zip: showNewFacility ? formData.new_facility_zip : null
      };

      const response = await axios.post(`${API}/health-systems/register`, payload);
      
      if (response.data.status === "success") {
        toast.success("Registration successful!");
        // Show access code in a modal or navigate to success page
        navigate("/hospital/register-success", { 
          state: { 
            accessCode: response.data.access_code,
            email: formData.email,
            healthSystem: healthSystem.system_name
          }
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl flex items-center gap-8">
        {/* Left side - Digital Isaac */}
        <div className="hidden lg:flex flex-col items-center justify-center flex-1">
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-radial from-cyan-500/20 via-blue-500/10 to-transparent blur-3xl scale-110" />
            
            {/* Isaac Image */}
            <img 
              src={DIGITAL_ISAAC}
              alt="Isaac - Your Healthcare Assistant"
              className="relative z-10 w-full max-w-md h-auto object-contain"
              style={{ 
                filter: 'drop-shadow(0 0 40px rgba(96, 165, 250, 0.3))',
                animation: 'float 6s ease-in-out infinite'
              }}
            />
          </div>
          
          {/* Welcome text */}
          <div className="text-center mt-6">
            <h2 className="text-2xl font-bold text-white mb-2">Meet Isaac</h2>
            <p className="text-slate-400 max-w-sm">Your intelligent healthcare operations assistant, ready to help streamline your facility management.</p>
          </div>
          
          <style>{`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-15px); }
            }
          `}</style>
        </div>
        
        {/* Right side - Registration Form */}
        <div className="w-full max-w-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Ask Isaac" className="h-14 mx-auto mb-4" />
            </Link>
            <h1 className="text-2xl font-bold text-white">Hospital Staff Registration</h1>
            <p className="text-slate-400 mt-2">Register with your health system email</p>
          </div>

          {/* Registration Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Progress Steps */}
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <div className="flex items-center justify-center gap-4">
                <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-slate-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                    {step > 1 ? <CheckCircle2 className="w-5 h-5" /> : "1"}
                  </div>
                  <span className="font-medium text-sm">Verify Email</span>
                </div>
                <div className="w-12 h-0.5 bg-slate-200" />
                <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-slate-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200'}`}>
                    2
                  </div>
                  <span className="font-medium text-sm">Complete Profile</span>
                </div>
              </div>
          </div>

          <div className="p-6">
            {step === 1 ? (
              /* Step 1: Email Verification */
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900">Enter Your Work Email</h2>
                  <p className="text-slate-500 mt-1 text-sm">
                    We&apos;ll verify your email domain to connect you with your health system
                  </p>
                </div>

                <div>
                  <Label>Work Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@yourorganization.org"
                    className="mt-1"
                    autoFocus
                  />
                </div>

                <Button 
                  onClick={checkEmailDomain} 
                  disabled={loading || !formData.email}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</>
                  ) : (
                    <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  Already registered?{" "}
                  <Link to="/hospital/login" className="text-blue-600 hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            ) : (
              /* Step 2: Registration Form */
              <form onSubmit={handleRegister} className="space-y-5">
                {/* Health System Banner */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-emerald-900">{healthSystem?.system_name}</p>
                    <p className="text-sm text-emerald-700">{formData.email}</p>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <Label className="flex items-center gap-1">
                    <User className="w-4 h-4 text-slate-400" />
                    Full Name *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Smith"
                    className="mt-1"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label className="flex items-center gap-1">
                    <Phone className="w-4 h-4 text-slate-400" />
                    Phone Number
                  </Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="713-555-1234"
                    className="mt-1"
                  />
                </div>

                {/* Title & Department */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      Job Title
                    </Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Manager"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Select 
                      value={formData.department} 
                      onValueChange={(v) => setFormData({...formData, department: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select department..." />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((dept) => (
                          <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Facility Selection */}
                <div>
                  <Label className="flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    Your Facility *
                  </Label>
                  
                  {!showNewFacility ? (
                    <div className="space-y-2 mt-1">
                      <Select 
                        value={formData.facility_id} 
                        onValueChange={(v) => setFormData({...formData, facility_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your facility..." />
                        </SelectTrigger>
                        <SelectContent>
                          {healthSystem?.facilities?.map((facility) => (
                            <SelectItem key={facility.id} value={facility.id}>
                              {facility.name} ({facility.city})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <button
                        type="button"
                        onClick={() => setShowNewFacility(true)}
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        My facility isn&apos;t listed - Add new
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Add New Facility</span>
                        <button
                          type="button"
                          onClick={() => setShowNewFacility(false)}
                          className="text-xs text-slate-500 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                      
                      <Input
                        value={formData.new_facility_name}
                        onChange={(e) => setFormData({...formData, new_facility_name: e.target.value})}
                        placeholder="Facility Name *"
                      />
                      
                      <Select 
                        value={formData.new_facility_type} 
                        onValueChange={(v) => setFormData({...formData, new_facility_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hospital">Hospital</SelectItem>
                          <SelectItem value="clinic">Clinic</SelectItem>
                          <SelectItem value="surgery_center">Surgery Center</SelectItem>
                          <SelectItem value="imaging_center">Imaging Center</SelectItem>
                          <SelectItem value="lab">Lab</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Input
                        value={formData.new_facility_address}
                        onChange={(e) => setFormData({...formData, new_facility_address: e.target.value})}
                        placeholder="Street Address"
                      />
                      
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          value={formData.new_facility_city}
                          onChange={(e) => setFormData({...formData, new_facility_city: e.target.value})}
                          placeholder="City"
                        />
                        <Input
                          value={formData.new_facility_state}
                          onChange={(e) => setFormData({...formData, new_facility_state: e.target.value})}
                          placeholder="State"
                        />
                        <Input
                          value={formData.new_facility_zip}
                          onChange={(e) => setFormData({...formData, new_facility_zip: e.target.value})}
                          placeholder="ZIP"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating Account...</>
                  ) : (
                    "Complete Registration"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setStep(1); setHealthSystem(null); }}
                  className="w-full text-sm text-slate-500 hover:underline"
                >
                  ← Use a different email
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Need help?{" "}
          <a href="mailto:support@sterilemate.com" className="text-blue-400 hover:underline">
            Contact Support
          </a>
        </p>
        </div>
      </div>
    </div>
  );
}
